import { useState, useCallback } from 'react';
import { FORM_STYLES } from '../constants/orderFormConstants';

/**
 * Custom hook for managing order form state
 * Uses objects for better organization and optimization
 */
export const useOrderFormState = () => {
  // Form data state - grouped logically
  const [formData, setFormData] = useState({
    quantity: "",
    youtube_url: "",
    service: "",
    product_id: null,
    service_type: "instant",
    serviceId: null,
    servicePrice: null,
    price: "0.00",
    category: "",
    selectedCategoryName: "",
  });

  // UI state - grouped logically
  const [uiState, setUiState] = useState({
    error2: "",
    isLoad: false,
    openSuccess: false,
    openError: false,
    isButtonVisible: false,
    showFullDescription: false,
    selectedServiceId: "", // Renamed from selectedServiceDescription
  });

  // Dropdown state - grouped logically
  const [dropdownState, setDropdownState] = useState({
    isOpenCat: false,
    isOpen: false,
  });

  // Helper functions to update state objects
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const updateUiState = useCallback((updates) => {
    setUiState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateDropdownState = useCallback((updates) => {
    setDropdownState(prev => ({ ...prev, ...updates }));
  }, []);

  // Reset service-related state
  const resetService = useCallback(() => {
    updateFormData({
      service: "",
      serviceId: null,
      servicePrice: null,
      product_id: null,
      selectedServiceId: null, // Renamed
      service_type: "instant",
    });
    updateUiState({
      showFullDescription: false,
    });
  }, [updateFormData, updateUiState]);

  // Reset entire form
  const resetForm = useCallback(() => {
    updateFormData({
      service: "",
      service_type: "instant",
      youtube_url: "",
      quantity: "",
      serviceId: null,
      servicePrice: null,
      product_id: null,
      selectedServiceId: null, // Renamed
      category: "",
      selectedCategoryName: "",
    });
    updateUiState({
      showFullDescription: false,
      isButtonVisible: false,
      error2: "",
    });
  }, [updateFormData, updateUiState]);

  // Update quantity with validation
  const updateQuantity = useCallback((value) => {
    updateFormData({ quantity: value });
  }, [updateFormData]);

  // Update service selection (only updates service name, other fields are set directly in OrderForm)
  const updateService = useCallback((value) => {
    updateFormData({ service: value });
  }, [updateFormData]);

  // Update category selection
  const updateCategory = useCallback((categoryName) => {
    updateFormData({
      category: categoryName,
      selectedCategoryName: categoryName,
    });
    updateDropdownState({ isOpenCat: false });
    resetService(); // Reset service when category changes
  }, [updateFormData, updateDropdownState, resetService]);

  return {
    // Form fields
    quantity: formData.quantity,
    setQuantity: updateQuantity,
    youtube_url: formData.youtube_url,
    setYoutube_Url: (value) => setFormData(prev => (prev.youtube_url !== value ? { ...prev, youtube_url: value } : prev)),
    service: formData.service,
    setService: updateService,
    product_id: formData.product_id,
    setProduct_id: (value) => setFormData(prev => (prev.product_id !== value ? { ...prev, product_id: value } : prev)),
    service_type: formData.service_type,
    setService_Type: (value) => setFormData(prev => (prev.service_type !== value ? { ...prev, service_type: value } : prev)),
    serviceId: formData.serviceId,
    setServiceId: (value) => setFormData(prev => (prev.serviceId !== value ? { ...prev, serviceId: value } : prev)),
    servicePrice: formData.servicePrice,
    setServicePrice: (value) => setFormData(prev => (prev.servicePrice !== value ? { ...prev, servicePrice: value } : prev)),
    price: formData.price,
    setprice: (value) => setFormData(prev => (prev.price !== value ? { ...prev, price: value } : prev)),
    category: formData.category,
    selectedCategoryName: formData.selectedCategoryName,
    setCategory: updateCategory,

    // UI states
    error2: uiState.error2,
    setError2: (value) => setUiState(prev => (prev.error2 !== value ? { ...prev, error2: value } : prev)),
    isLoad: uiState.isLoad,
    setIsLoad: (value) => setUiState(prev => (prev.isLoad !== value ? { ...prev, isLoad: value } : prev)),
    openSuccess: uiState.openSuccess,
    setOpenSuccess: (value) => setUiState(prev => (prev.openSuccess !== value ? { ...prev, openSuccess: value } : prev)),
    openError: uiState.openError,
    setOpenError: (value) => setUiState(prev => (prev.openError !== value ? { ...prev, openError: value } : prev)),
    isButtonVisible: uiState.isButtonVisible,
    setIsButtonVisible: (value) => setUiState(prev => (prev.isButtonVisible !== value ? { ...prev, isButtonVisible: value } : prev)),
    showFullDescription: uiState.showFullDescription,
    setShowFullDescription: (value) => setUiState(prev => (prev.showFullDescription !== value ? { ...prev, showFullDescription: value } : prev)),
    selectedServiceId: uiState.selectedServiceId, // Renamed
    setSelectedServiceId: (value) => setUiState(prev => (prev.selectedServiceId !== value ? { ...prev, selectedServiceId: value } : prev)), // Renamed

    // Dropdown states
    isOpenCat: dropdownState.isOpenCat,
    setIsOpenCat: (value) => setDropdownState(prev => (prev.isOpenCat !== value ? { ...prev, isOpenCat: value } : prev)),
    isOpen: dropdownState.isOpen,
    setIsOpen: (value) => setDropdownState(prev => (prev.isOpen !== value ? { ...prev, isOpen: value } : prev)),

    // Helper functions
    resetService,
    resetForm,
  };
};
